import { motion } from 'framer-motion';

// Shared container style for consistency
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Global Reach (Connection Map)
export const GlobalReachVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col items-center justify-center relative overflow-hidden text-center">

                {/* Orbital Rings (Background) */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-60">
                    <div className="absolute w-40 h-40 sm:w-64 sm:h-64 rounded-full border border-dashed border-blue-100 animate-[spin_20s_linear_infinite]" />
                    <div className="absolute w-32 h-32 sm:w-48 sm:h-48 rounded-full border border-blue-50 animate-[spin_15s_linear_infinite_reverse]" />
                </div>

                {/* Central Master Deck (Detailed) */}
                <div className="relative z-10 flex flex-col items-center">
                    <motion.div
                        initial={{ scale: 0.9 }}
                        animate={{ scale: [0.9, 1, 0.9] }}
                        transition={{ duration: 4, repeat: Infinity }}
                        className="w-24 aspect-[3/4] bg-white rounded-lg border border-blue-100 shadow-xl relative overflow-hidden group"
                    >
                        {/* Slide Cover */}
                        <div className="h-1/2 bg-blue-600/5 p-2 flex flex-col justify-center items-center gap-1">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 0L24 12L12 24L0 12L12 0Z" />
                                </svg>
                            </div>
                        </div>
                        {/* Slide Info */}
                        <div className="p-2 space-y-1 text-left">
                            <div className="h-1.5 w-16 bg-gray-200 rounded-full" />
                            <div className="h-1 w-12 bg-gray-100 rounded-full" />
                        </div>
                        {/* Status Badge */}
                        <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-blue-600 text-white text-[8px] font-bold rounded shadow-sm border border-white/20">
                            MASTER
                        </div>
                    </motion.div>
                </div>

                {/* Satellite Regions (Orbiting) */}
                {/* We use absolute positioning relative to the center */}
                <div className="absolute inset-0 pointer-events-none">
                    <RegionNode top="15%" left="50%" label="English" region="LDN" delay={0} color="bg-blue-50 border-blue-100 text-blue-700" />
                    <RegionNode top="60%" left="15%" label="Deutsch" region="BER" delay={1} color="bg-orange-50 border-orange-100 text-orange-700" />
                    <RegionNode top="60%" right="15%" label="日本語" region="TYO" delay={2} color="bg-red-50 border-red-100 text-red-700" />
                </div>

            </div>
        </VisualContainer>
    );
};

const RegionNode = ({ top, left, right, label, region, delay, color }: any) => (
    <motion.div
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{ delay, type: "spring" }}
        className={`absolute flex flex-col items-center gap-1.5 z-20`}
        style={{ top, left, right, transform: 'translateX(-50%)' }}
    >
        <div className={`px-3 py-1.5 rounded-full shadow-sm border ${color} bg-white flex items-center gap-2`}>
            <span className="text-[10px] font-bold">{label}</span>
            <span className="text-[8px] font-medium opacity-60 uppercase tracking-wider border-l border-gray-200 pl-2">{region}</span>
        </div>
        {/* Connection Line to Center (Visual only, simple) */}
        <div className="w-0.5 h-8 bg-gradient-to-b from-current to-transparent opacity-20" />
    </motion.div>
);

// Component 2: Cultural Adaptation (Text Transformation)
export const CulturalAdaptationVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 relative flex flex-col justify-center gap-4 sm:gap-8">

                {/* Input: Casual/Western */}
                <div className="flex items-start gap-3 opacity-50">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 text-lg">🇺🇸</div>
                    <div className="bg-gray-50 rounded-lg rounded-tl-none p-3 text-sm text-gray-500 font-medium">
                        "We absolutely crushed it this quarter!"
                    </div>
                </div>

                {/* Processing/Transformation */}
                <div className="flex justify-center -my-2 relative z-10">
                    <motion.div
                        initial={{ rotate: 0 }}
                        whileInView={{ rotate: 180 }}
                        transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
                        className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-500 border border-blue-100 shadow-sm"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </motion.div>
                </div>

                {/* Output: Formal/Cultural Match */}
                <div className="flex items-start gap-3 justify-end">
                    <div className="bg-blue-50 rounded-lg rounded-tr-none p-3 text-sm text-gray-800 font-medium shadow-sm border border-blue-100 relative">
                        "Surpassed performance targets significantly."
                        {/* Badge */}
                        <motion.div
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            transition={{ delay: 0.5, type: 'spring' }}
                            className="absolute -bottom-3 -right-2 bg-white text-[10px] font-bold text-blue-600 px-2 py-0.5 rounded-full shadow-md border border-gray-100 flex items-center gap-1"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            Professional (JP)
                        </motion.div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center shrink-0 text-lg border border-red-100">🇯🇵</div>
                </div>

            </div>
        </VisualContainer>
    );
};
